/**
 * Web OAuth popup completion helpers.
 * Expo opens Google auth in a popup; the callback must stay in the popup, notify the
 * opener, and close. COOP often clears window.opener — we persist the callback URL and
 * use BroadcastChannel + a dedicated storage key Expo does not clear on dismiss.
 */

const REDIRECT_HANDLE_KEY = 'ExpoWebBrowserRedirectHandle';
const PENDING_OAUTH_URL_KEY = 'SmartSaaS_oauth_callback_url';
const OAUTH_CALLBACK_EVENT = 'smartsaas-oauth-callback';

function originUrlKey(handle) {
  return `ExpoWebBrowser_OriginUrl_${handle}`;
}

function redirectUrlKey(handle) {
  return `ExpoWebBrowser_RedirectUrl_${handle}`;
}

/** Google OAuth callbacks include state= plus code= or #access_token= (web uses implicit/hash). */
function isOAuthCallbackUrl(href) {
  if (typeof window === 'undefined') return false;
  const url = href || window.location.href;
  let search = '';
  let hash = '';
  try {
    const parsed = new URL(url, window.location.origin);
    search = parsed.search || '';
    hash = parsed.hash || '';
  } catch (_) {
    search = window.location.search || '';
    hash = window.location.hash || '';
  }
  const combined = search + hash;
  return (
    combined.includes('state=') &&
    (search.includes('code=') || hash.includes('access_token='))
  );
}

function isMobileWebBrowser() {
  if (typeof navigator === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|Mobile/i.test(navigator.userAgent || '');
}

function saveOAuthCallbackUrl(url) {
  if (typeof window === 'undefined' || !url) return;
  window.localStorage.setItem(PENDING_OAUTH_URL_KEY, url);
  const handle = window.localStorage.getItem(REDIRECT_HANDLE_KEY);
  if (handle) {
    window.localStorage.setItem(originUrlKey(handle), url);
  }
}

function clearOAuthSessionStorage() {
  if (typeof window === 'undefined') return;
  const handle = window.localStorage.getItem(REDIRECT_HANDLE_KEY);
  window.localStorage.removeItem(PENDING_OAUTH_URL_KEY);
  window.localStorage.removeItem(REDIRECT_HANDLE_KEY);
  if (handle) {
    window.localStorage.removeItem(originUrlKey(handle));
    window.localStorage.removeItem(redirectUrlKey(handle));
  }
}

function isOAuthPopupWindow() {
  if (typeof window === 'undefined') return false;
  return !!(window.opener && !window.opener.closed) || window.name === 'smartsaas-google-oauth';
}

function notifyOAuthOpener(url) {
  if (typeof window === 'undefined' || !url) return;
  const handle = window.localStorage.getItem(REDIRECT_HANDLE_KEY);

  if (window.opener && !window.opener.closed) {
    try {
      window.opener.postMessage({ url, expoSender: handle }, window.location.origin);
    } catch (_) {
      try {
        window.opener.postMessage({ url, expoSender: handle }, '*');
      } catch (_) {}
    }
  }

  if (typeof BroadcastChannel !== 'undefined') {
    try {
      const channel = new BroadcastChannel('ExpoWebBrowserRedirect');
      channel.postMessage({ url, expoSender: handle });
      channel.close();
    } catch (_) {}
  }

  try {
    window.dispatchEvent(new CustomEvent(OAUTH_CALLBACK_EVENT, { detail: { url } }));
  } catch (_) {}
}

/**
 * Requests this tab actually prompted with. Expo can rebuild the AuthRequest (new state)
 * while the popup is open, so the callback must also be checked against these.
 */
const promptedRequests = [];
const MAX_PROMPTED_REQUESTS = 5;

function requestSignature(request) {
  if (!request) return '';
  const scopes = Array.isArray(request.scopes) ? request.scopes.join(' ') : '';
  return [request.clientId, request.responseType, request.redirectUri, scopes].join('|');
}

function rememberPromptedRequest(request) {
  if (!request?.parseReturnUrl || promptedRequests.includes(request)) return;
  promptedRequests.push(request);
  if (promptedRequests.length > MAX_PROMPTED_REQUESTS) promptedRequests.shift();
}

function forgetPromptedRequests(request) {
  const signature = requestSignature(request);
  for (let i = promptedRequests.length - 1; i >= 0; i -= 1) {
    if (requestSignature(promptedRequests[i]) === signature) promptedRequests.splice(i, 1);
  }
}

/**
 * Parse against the current request and any prompted request of the same provider config.
 * Returns null when the callback belongs to another request (state mismatch) so callers
 * never consume a callback that another window or button is waiting for.
 */
function tryParseOAuthReturn(request, url) {
  if (!request?.parseReturnUrl || !url) return null;
  const signature = requestSignature(request);
  const candidates = [request].concat(
    promptedRequests.filter((r) => r !== request && requestSignature(r) === signature)
  );
  let providerError = null;
  for (const candidate of candidates) {
    let parsed;
    try {
      parsed = candidate.parseReturnUrl(url);
    } catch (_) {
      continue;
    }
    if (parsed?.type === 'success') return parsed;
    if (parsed?.type === 'error' && parsed.error?.code !== 'state_mismatch' && !providerError) {
      providerError = parsed;
    }
  }
  return providerError;
}

function readPendingOAuthCallbackUrl() {
  if (typeof window === 'undefined') return null;
  const pending = window.localStorage.getItem(PENDING_OAUTH_URL_KEY);
  if (pending) return pending;
  const handle = window.localStorage.getItem(REDIRECT_HANDLE_KEY);
  return handle ? window.localStorage.getItem(originUrlKey(handle)) : null;
}

/** Close this window (popup). Retries — COOP can delay close until after redirect settles. */
function closeOAuthPopupWindow() {
  if (typeof window === 'undefined') return;
  try {
    document.documentElement.style.visibility = 'hidden';
  } catch (_) {}
  const attempt = () => {
    try {
      window.close();
    } catch (_) {}
  };
  attempt();
  setTimeout(attempt, 80);
  setTimeout(attempt, 400);
  setTimeout(attempt, 1200);
}

/**
 * Callback page: resolve true once another tab consumed the stored callback URL.
 * Resolves false when nothing was stored or nobody consumed it within timeoutMs,
 * in which case this page should mount the app and handle the callback itself.
 */
function waitForOAuthCallbackHandoff(timeoutMs = 5000) {
  if (typeof window === 'undefined' || !window.localStorage.getItem(PENDING_OAUTH_URL_KEY)) {
    return Promise.resolve(false);
  }
  return new Promise((resolve) => {
    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (!window.localStorage.getItem(PENDING_OAUTH_URL_KEY)) {
        clearInterval(timer);
        resolve(true);
      } else if (Date.now() - startedAt >= timeoutMs) {
        clearInterval(timer);
        resolve(false);
      }
    }, 100);
  });
}

/** Parent tab: close the popup Expo opened via window.open. */
function dismissOAuthPopupFromOpener() {
  if (typeof window === 'undefined') return;
  try {
    const WebBrowser = require('expo-web-browser');
    WebBrowser.dismissAuthSession();
  } catch (_) {}
}

/**
 * Run in the OAuth popup (or callback page) before React mounts.
 * Returns true when this page handled a popup callback and should close.
 */
function completeOAuthPopupSessionIfNeeded() {
  if (typeof window === 'undefined') return false;
  if (!isOAuthCallbackUrl()) return false;

  const url = window.location.href;
  saveOAuthCallbackUrl(url);
  notifyOAuthOpener(url);

  let expoCompleted = false;
  try {
    const WebBrowser = require('expo-web-browser');
    const result = WebBrowser.maybeCompleteAuthSession({ skipRedirectCheck: true });
    expoCompleted = result?.type === 'success';
  } catch (_) {
    // ERR_WEB_BROWSER_REDIRECT when window.opener is null under COOP — expected.
  }

  if (!expoCompleted) {
    notifyOAuthOpener(url);
  }

  const handle = window.localStorage.getItem(REDIRECT_HANDLE_KEY);
  // Opener/name are often cleared after Google redirect; handle + callback URL means popup.
  const shouldClosePopup = isOAuthPopupWindow() || !!handle;
  if (shouldClosePopup) {
    closeOAuthPopupWindow();
    return true;
  }
  return false;
}

/**
 * Main tab landed with OAuth params (same-window redirect). Persist for the login UI.
 */
function stashMainTabOAuthCallbackIfNeeded() {
  if (typeof window === 'undefined') return false;
  if (!isOAuthCallbackUrl()) return false;
  if (isOAuthPopupWindow()) return false;

  const url = window.location.href;
  saveOAuthCallbackUrl(url);
  notifyOAuthOpener(url);

  try {
    const parsed = new URL(url);
    window.history.replaceState(null, '', parsed.pathname + parsed.search);
  } catch (_) {
    try {
      window.history.replaceState(null, '', window.location.pathname + (window.location.search || ''));
    } catch (_) {}
  }
  return true;
}

/**
 * Recover OAuth result on the opener when promptAsync returned dismiss/cancel but the
 * popup wrote the callback URL to storage (COOP / dismiss race fallback).
 */
function recoverOAuthResultFromStorage(request) {
  if (typeof window === 'undefined' || !request?.parseReturnUrl) return null;

  const url = readPendingOAuthCallbackUrl();
  if (!url) return null;

  const parsed = tryParseOAuthReturn(request, url);
  if (parsed) {
    clearOAuthSessionStorage();
    forgetPromptedRequests(request);
  }
  return parsed;
}

/**
 * Wrap promptAsync on web: listen for postMessage / BroadcastChannel + recover from storage.
 */
function listenForOAuthPopupResult(request, promptAsyncFn) {
  if (typeof window === 'undefined') {
    return promptAsyncFn();
  }

  rememberPromptedRequest(request);

  return new Promise((resolve) => {
    let settled = false;
    let recoveryPoll = null;

    const finish = (result) => {
      if (settled) return;
      settled = true;
      clearInterval(pollInterval);
      if (recoveryPoll) clearInterval(recoveryPoll);
      window.removeEventListener('message', messageListener);
      channel?.close();
      if (result?.type === 'success' || result?.type === 'error') {
        dismissOAuthPopupFromOpener();
      }
      resolve(result);
    };

    const tryRecover = () => {
      const recovered = recoverOAuthResultFromStorage(request);
      if (recovered?.type === 'success' || recovered?.type === 'error') {
        finish(recovered);
        return true;
      }
      return false;
    };

    const messageListener = (event) => {
      if (!event?.data?.url || event.origin !== window.location.origin) return;
      const parsed = tryParseOAuthReturn(request, event.data.url);
      if (parsed) {
        clearOAuthSessionStorage();
        forgetPromptedRequests(request);
        finish(parsed);
      }
    };
    window.addEventListener('message', messageListener, false);

    let channel;
    if (typeof BroadcastChannel !== 'undefined') {
      channel = new BroadcastChannel('ExpoWebBrowserRedirect');
      channel.onmessage = (event) => {
        if (!event.data?.url) return;
        const parsed = tryParseOAuthReturn(request, event.data.url);
        if (parsed) {
          clearOAuthSessionStorage();
          forgetPromptedRequests(request);
          finish(parsed);
        }
      };
    }

    const pollInterval = setInterval(() => {
      if (settled) return;
      tryRecover();
    }, 100);

    const startRecoveryPoll = (fallbackResult) => {
      let attempts = 0;
      recoveryPoll = setInterval(() => {
        if (settled) return;
        attempts += 1;
        if (tryRecover()) return;
        if (attempts >= 30) {
          finish(fallbackResult);
        }
      }, 100);
    };

    promptAsyncFn()
      .then((result) => {
        if (result?.type === 'success') {
          finish(result);
          return;
        }
        if (tryRecover()) return;
        startRecoveryPoll(result);
      })
      .catch((err) => finish({ type: 'error', error: err }));
  });
}

function subscribeOAuthCallback(request, onResult) {
  if (typeof window === 'undefined' || !request) return () => {};

  const handleUrl = (url) => {
    if (!url) return;
    const parsed = tryParseOAuthReturn(request, url);
    if (parsed?.type === 'success') {
      clearOAuthSessionStorage();
      forgetPromptedRequests(request);
      dismissOAuthPopupFromOpener();
      onResult(parsed);
    }
  };

  const handler = (event) => handleUrl(event?.detail?.url);

  // Late callbacks from a popup tab that lost window.opener (COOP, embedded browsers)
  // arrive after listenForOAuthPopupResult has already settled on dismiss.
  const storageHandler = (event) => {
    if (!event?.key || !event.newValue) return;
    if (event.key === PENDING_OAUTH_URL_KEY || event.key.startsWith('ExpoWebBrowser_OriginUrl_')) {
      handleUrl(event.newValue);
    }
  };

  let channel = null;
  if (typeof BroadcastChannel !== 'undefined') {
    try {
      channel = new BroadcastChannel('ExpoWebBrowserRedirect');
      channel.onmessage = (event) => handleUrl(event?.data?.url);
    } catch (_) {
      channel = null;
    }
  }

  window.addEventListener(OAUTH_CALLBACK_EVENT, handler);
  window.addEventListener('storage', storageHandler);
  const pending = recoverOAuthResultFromStorage(request);
  if (pending?.type === 'success') {
    dismissOAuthPopupFromOpener();
    onResult(pending);
  }

  return () => {
    window.removeEventListener(OAUTH_CALLBACK_EVENT, handler);
    window.removeEventListener('storage', storageHandler);
    channel?.close();
  };
}

module.exports = {
  isOAuthCallbackUrl,
  isOAuthPopupWindow,
  isMobileWebBrowser,
  saveOAuthCallbackUrl,
  closeOAuthPopupWindow,
  dismissOAuthPopupFromOpener,
  completeOAuthPopupSessionIfNeeded,
  stashMainTabOAuthCallbackIfNeeded,
  recoverOAuthResultFromStorage,
  listenForOAuthPopupResult,
  subscribeOAuthCallback,
  clearOAuthSessionStorage,
  waitForOAuthCallbackHandoff,
};
